/**
 * Guide engine v3 overlay - reduced-motion (#1025, epic #1020). Spec R15 / R17.
 *
 * The popup fade+scale, the ring pulse, and the Wheel-of-Fortune staggered reveal
 * all honor `prefers-reduced-motion`: reduced motion = instant popup, steady glow,
 * all rings at once. Each visual reads `useResolvedReducedMotion()` so a test can
 * force either branch without stubbing matchMedia.
 */

import { useEffect, useState } from 'react';

/** The reduce media query, or null when matchMedia is absent / a partial stub. */
const reduceQuery = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mq && typeof mq.matches === 'boolean' ? mq : null;
};

/** Snapshot of the OS reduced-motion preference (false when matchMedia is absent). */
export const prefersReducedMotion = () => {
  const mq = reduceQuery();
  return mq ? mq.matches : false;
};

/** Live reduced-motion preference, updating if the user flips the OS setting. */
export const useReducedMotion = () => {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    const mq = reduceQuery();
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); };
  }, []);
  return reduced;
};

/**
 * Resolve the active reduced-motion flag: an explicit `override` (used by tests)
 * wins, otherwise the live OS preference. Always calls the hook so the rule of
 * hooks holds regardless of the override.
 * @param {boolean|undefined} override
 * @returns {boolean}
 */
export const useResolvedReducedMotion = (override) => {
  const auto = useReducedMotion();
  return override === undefined ? auto : override;
};
